import React, { useEffect, useState } from 'react';
import { storage } from '../services/storage';
import { API_BASE_URL } from '../utils/apiConfig';

interface HowItWentSectionProps {
  activityId: string;
  className?: string;
}

interface AnalysisData {
  activity_id: string;
  cedar_insights: string[] | null;
  workout_summary: string | null;
  key_observations: string[] | null;
  debrief_started_at: string | null;
  created_at: string;
  synthesized_at: string | null;
}

type LoadingState = 'idle' | 'loading' | 'success' | 'no-data' | 'error';

/**
 * HowItWentSection Component
 * 
 * Displays Coach Cedar's permanent insights from post-workout analysis and debrief.
 * - Fetches from /api/workouts/{activity_id}/analysis
 * - Shows 3 bullets from cedar_insights array
 * - Only displays after synthesis completes (~30 min after debrief)
 * - Handles loading, no data, error, and success states
 * 
 * Spec: WORKOUT_CARD_DEBRIEF_SECTION_SPEC.md
 */
export const HowItWentSection: React.FC<HowItWentSectionProps> = ({ activityId, className }) => {
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');
  const [analysisData, setAnalysisData] = useState<AnalysisData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchAnalysis = async () => {
      setLoadingState('loading');

      const token = await storage.getItem('jwt_token');
      if (!token) {
        setLoadingState('error');
        return;
      }

      // Retry with backoff: synthesis takes ~1 min after debrief
      const delays = [0, 10000, 30000, 60000]; // 4 attempts, ~100s max

      for (let attempt = 0; attempt < delays.length; attempt++) {
        if (cancelled) return;
        if (attempt > 0) {
          await new Promise(resolve => setTimeout(resolve, delays[attempt]));
        }
        if (cancelled) return;

        try {
          const response = await fetch(
            `${API_BASE_URL}/api/workouts/${activityId}/analysis`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );

          if (response.status === 404) {
            if (attempt < delays.length - 1) continue; // Retry — analysis may not exist yet
            setLoadingState('no-data');
            return;
          }

          if (!response.ok) {
            if (attempt < delays.length - 1) continue;
            throw new Error(`API error: ${response.status}`);
          }

          const data = await response.json();

          // Show the card as soon as we have any analysis content:
          // cedar_insights (post-debrief synthesis) OR key_observations (initial analysis).
          const hasContent =
            (data.cedar_insights && data.cedar_insights.length > 0) ||
            (data.key_observations && data.key_observations.length > 0);

          if (!hasContent) {
            if (attempt < delays.length - 1) continue; // Retry — analysis may not have run yet
            setLoadingState('no-data');
            return;
          }

          if (cancelled) return;
          setAnalysisData(data);
          setLoadingState('success');
          return; // Success — stop retrying
        } catch (err) {
          if (attempt === delays.length - 1) {
            console.error('Failed to fetch workout analysis after retries:', err);
            setError(err instanceof Error ? err.message : 'Unknown error');
            setLoadingState('error');
          }
        }
      }
    };

    fetchAnalysis();
    return () => { cancelled = true; };
  }, [activityId]);

  // Loading state
  if (loadingState === 'loading') {
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-lg animate-pulse">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
          <div className="h-4 bg-gray-300 rounded w-24"></div>
          <div className="flex-1 h-px bg-gray-300"></div>
        </div>
        <div className="space-y-2">
          <div className="h-3 bg-gray-300 rounded w-full"></div>
          <div className="h-3 bg-gray-300 rounded w-5/6"></div>
          <div className="h-3 bg-gray-300 rounded w-4/5"></div>
        </div>
      </div>
    );
  }

  // No data state (synthesis hasn't completed yet)
  if (loadingState === 'no-data') {
    return null; // Per spec: section doesn't appear until synthesis completes
  }

  // Error state (silent failure - don't break the UI)
  if (loadingState === 'error') {
    console.error('HowItWentSection error:', error);
    return null; // Silent failure per spec
  }

  // Success state — two sub-states:
  // 1. cedar_insights present: post-debrief synthesis (final)
  // 2. cedar_insights absent but key_observations present: initial analysis (pre-debrief)
  if (loadingState === 'success' && analysisData) {
    const isSynthesized = analysisData.cedar_insights && analysisData.cedar_insights.length > 0;
    const bullets = isSynthesized
      ? analysisData.cedar_insights!
      : (analysisData.key_observations || []);

    if (bullets.length === 0) return null;

    return (
      <div className={`mt-4 p-4 bg-white border border-gray-200 rounded-lg ${className || ''}`}>
        {/* Section Header */}
        <div className="flex items-center gap-2 mb-3">
          <div className="w-1.5 h-1.5 bg-[#1F6C4F] rounded-full"></div>
          <h3 className="text-sm font-semibold text-[#1F6C4F]">
            How It Went
          </h3>
          <div className="flex-1 h-px bg-gray-200"></div>
        </div>

        {/* Insights Display */}
        <div className="space-y-2">
          {bullets.map((insight, index) => (
            <div key={index} className="flex items-start gap-2">
              <div className="w-1.5 h-1.5 mt-2 bg-[#1F6C4F] rounded-full flex-shrink-0"></div>
              <p className="text-sm text-gray-700 leading-relaxed">
                {insight}
              </p>
            </div>
          ))}
        </div>

        {/* Attribution Footer */}
        <div className="mt-3 text-xs text-gray-400 italic">
          {isSynthesized ? "Cedar's analysis from your debrief" : "Cedar's initial analysis"}
        </div>
      </div>
    );
  }

  return null;
};
